import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, getFormValues, FormSection } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import messages from '../../../text/messages';
import { normalizeEIN } from './../../../utilities/string';
import { TextField, SelectField } from './../../../components/fields';
import { required } from './../../../utilities/validation';
import Table from './../../../components/table';
import WizardButtons from './../../../components/wizard/navigator/buttons';
import { getAddresses, saveAddress } from './../../../reducers/addresses';
import { updateUserDetails } from './../../../reducers/identity';
import { getUsers, usersAreLoaded } from '../../../reducers/users';
import { ConfirmationDialog } from '../../../components/dialogs';
import { Snackbar } from '../../../components/snackbar';
import { anythingChanged } from '../../../utilities/common';
import AddressComponent from '../../../components/address';

class CompanyInformation extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            modal: { open: false, values: undefined }
        };

        this.renderPrimaryContactData = this.renderPrimaryContactData.bind(this);
        this.renderAddressFields = this.renderAddressFields.bind(this);
        this.submit = this.submit.bind(this);

        // modal controls
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentDidMount() {
        let { role, users, usersRole, addresses, getAddresses, getUsers } = this.props;
        if (!users || !usersAreLoaded(role, usersRole)) getUsers(role);
        if (!addresses) getAddresses(role);
    }

    onOpen(values) {
        this.setState({ modal: { open: true, values } });
    }

    onClose() {
        this.setState({ modal: { open: false, values: undefined } });
    }

    submit(values, action) {
        const { role, client, saveAddress, updateUserDetails, getAddresses } = this.props;
        const { name, fein, web_site, address, contact_user } = values;

        let saveStepData = data => {
            updateUserDetails(role, { ...client, ...data, address: undefined }, () => getAddresses(role, action));
        };

        saveAddress({ ...address, ref_id: role.id, ref_type: role.type, kind: 'p' }, result => {
            let data = { name, fein, web_site, contact_user, address: { ...address, id: result.id } };
            saveStepData(data);
        });
    }

    renderPrimaryContactData() {
        const { profile } = this.props;
        const { first_name, last_name, email, phone, phone_ext } = profile;
        let phoneNumber = phone ? `${phone_ext ? `${phone_ext} ` : ''} ${phone}` : <FormattedMessage {...messages.notProvided} />;

        return (
            <Table
                rowStyle="onboarding-table-row"
                columns={[
                    { valueMap: d => <span className="row-label">{d.label}</span>, size: 'col-xs-12 col-md-3' },
                    { valueMap: d => <span className="row-value">{d.value}</span>, size: 'col-xs-12 col-md-9' }
                ]}
                data={[
                    { label: <FormattedMessage {...messages.firstName} />, value: first_name },
                    { label: <FormattedMessage {...messages.lastName} />, value: last_name },
                    { label: <FormattedMessage {...messages.email} />, value: email },
                    { label: <FormattedMessage {...messages.phoneNumber} />, value: phoneNumber }
                ]}
            />
        );
    }

    renderAddressFields() {
        const { form } = this.props;
        return (
            <div className="row">
                <div className="col-xs-12 col-sm-8">
                    <FormSection name={'address'}>
                        <div className="row">
                            <AddressComponent form={form} section={'address'} hideName={true} />
                        </div>
                    </FormSection>
                </div>
            </div>
        );
    }

    render() {
        const { modal } = this.state;
        const { handleSubmit, submitting, addresses, users, isPristine } = this.props;
        if (!addresses || !users) {
            return null;
        }

        return (
            <React.Fragment>
                <form onSubmit={handleSubmit(values => this.onOpen(values))}>
                    <div className="row">
                        <div className="col-xs-12 col-sm-8">
                            <Field name="name" validate={required} component={TextField} placeholder={messages.companyName} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xs-12 col-sm-4">
                            <Field name="fein" validate={required} normalize={normalizeEIN} component={TextField} placeholder={messages.companyTaxId} />
                        </div>
                        <div className="col-xs-12 col-sm-4">
                            <Field name="web_site" validate={required} component={TextField} placeholder={messages.companyURL} />
                        </div>
                    </div>

                    {this.renderAddressFields()}

                    <div className="row section">
                        <div className="col-xs-9 col-sm-4">
                            <Field
                                name="contact_user"
                                component={SelectField}
                                options={users.map(user => ({ value: user.id, label: `${user.first_name} ${user.last_name}` }))}
                                placeholder={messages.contactPerson}
                                validate={required}
                            />
                        </div>
                        <div className="col-xs-3 col-sm-2">
                            <button type="button" className="field-inline-button" onClick={this.onOpen}>
                                <FormattedMessage {...messages.add} />
                            </button>
                        </div>
                    </div>

                    <WizardButtons disabled={submitting || isPristine} submit={{ message: <FormattedMessage {...messages.update} /> }} />
                </form>
                <ConfirmationDialog
                    modal={modal}
                    message={<FormattedMessage {...messages.updatingConfirmation} values={{ entity: 'company information' }} />}
                    onClose={this.onClose}
                    onSubmit={() => {
                        this.submit(modal.values, () => {
                            this.onClose();
                            Snackbar.success(<FormattedMessage {...messages.companyInfoUpdated} />);
                        });
                    }}
                />
            </React.Fragment>
        );
    }
}

CompanyInformation = reduxForm({
    form: 'client-company-info',
    destroyOnUnmount: false,
    keepDirtyOnReinitialize: true,
    enableReinitialize: true,
    forceUnregisterOnUnmount: false
})(CompanyInformation);
const mapStateToProps = state => {
    const getAddressFields = address => {
        if (!address) return address;
        const { id, ref_id, ref_type, kind, name, street, street2, city, state, country, zip } = address;
        return { id, ref_id, ref_type, kind, name, street, street2, city, state, country, zip };
    };

    let client = state.identity.details;
    let addresses = state.addresses.list;
    let initialValues = {
        name: client.name,
        fein: client.fein,
        web_site: client.web_site,
        contact_user: client.contact_user,
        address: addresses && addresses.length > 0 ? getAddressFields(addresses[0]) : undefined
    };
    const formValues = getFormValues('client-company-info')(state);
    if (formValues && formValues.address) {
        formValues.address = getAddressFields(formValues.address);
    }

    return {
        role: state.identity.role,
        profile: state.identity.profile,
        client,
        addresses,
        users: state.users.list,
        usersRole: state.users.listRole,
        initialValues,
        isPristine: anythingChanged({ initialValues, values: formValues })
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getAddresses: getAddresses,
            saveAddress: saveAddress,
            updateUserDetails: updateUserDetails,
            getUsers: getUsers
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(CompanyInformation);



// WEBPACK FOOTER //
// ./src/pages/company/forms/companyInformation.jsx